/**
 * Pure layout math for {@link DayTimeline}. Kept separate from the React
 * component so the overlap / column-packing / day-filtering logic is unit
 * testable (the electron vitest config only includes `*.test.ts`, not `.tsx`).
 *
 * All math operates on local wall-clock time (`Date.getHours()` etc.), matching
 * the editable-datetime picker which edits in the user's local timezone.
 */

/** A time interval, clamped to a single day's [start, end] in epoch ms. */
export interface DaySpan {
  /** Epoch ms of the interval start, clamped to >= start-of-day. */
  startMs: number
  /** Epoch ms of the interval end, clamped to <= end-of-day. */
  endMs: number
}

/** An existing event reduced to what the timeline needs to render it. */
export interface TimelineEvent {
  id: string
  title: string
  start: Date
  end: Date
}

/** A laid-out block: the source event plus its vertical span and column slot. */
export interface PositionedEvent extends DaySpan {
  event: TimelineEvent
  /** 0-based column index within its overlap cluster. */
  column: number
  /** Total number of columns in this block's overlap cluster. */
  columnCount: number
  /** True when this event overlaps the proposed slot. */
  conflict: boolean
}

const MS_PER_MINUTE = 60_000
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE

/** Local midnight (start-of-day) for the given date. */
export function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Returns true when `a` and `b` fall on the same local calendar day. */
export function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/**
 * Half-open interval overlap: [aStart, aEnd) ∩ [bStart, bEnd) ≠ ∅.
 * Zero-length intervals never overlap (a point touching an edge is not a clash).
 */
export function intervalsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd
}

/**
 * Identity of the event currently being edited, used to keep it from appearing
 * in the timeline as one of the user's "other" events (and conflicting with
 * itself). When rescheduling an existing meeting, the *original* (pre-edit)
 * start/end still matches its synced calendar row even after the user drags the
 * draft to a new time, so we match on those original instants. A brand-new
 * create has no synced row, so nothing matches and nothing is wrongly excluded.
 */
export interface EditedEventIdentity {
  /** Epoch ms of the edited event's ORIGINAL (seed) start instant. */
  startMs: number
  /** Epoch ms of the edited event's ORIGINAL (seed) end instant. */
  endMs: number
}

/** Tolerance for matching the edited event's instants against a synced row. */
const MATCH_TOLERANCE_MS = MS_PER_MINUTE

/**
 * Drop the event under edit from the calendar set so it never renders as an
 * "other" event or self-conflicts in its own picker. The edited event is a SINGLE
 * synced row, but it may sit at different instants over its lifetime — its
 * original time, or (after a save + edit-options refresh) its committed time,
 * until the calendar re-polls. Pass the candidate identities in PRIORITY order;
 * we remove AT MOST ONE row: the first match of the first identity that matches.
 * So an unrelated meeting that happens to share a slot still shows (and still
 * counts toward conflicts), and two rows are never dropped for one edit. Matching
 * is on start AND end within a one-minute tolerance (to absorb RFC3339 offset
 * round-tripping / second-level drift). Returns the input unchanged when no
 * identity matches (e.g. a brand-new create with no synced row).
 */
export function excludeEditedEvent(
  events: Array<TimelineEvent>,
  identities: Array<EditedEventIdentity>,
): Array<TimelineEvent> {
  for (const edited of identities) {
    const matchIndex = events.findIndex(
      (e) =>
        Math.abs(e.start.getTime() - edited.startMs) <= MATCH_TOLERANCE_MS &&
        Math.abs((e.end ?? e.start).getTime() - edited.endMs) <= MATCH_TOLERANCE_MS,
    )
    if (matchIndex !== -1) return events.filter((_, i) => i !== matchIndex)
  }
  return events
}

/**
 * True when the event's time interval intersects the given local day. Uses the
 * same half-open overlap test as the timeline layout, so a multi-day event that
 * spans this day (starting and ending on other local dates) still counts —
 * unlike an endpoint-on-day check, which would drop it. Zero/negative-length
 * events get a 1ms floor so a point exactly at the day's start still matches.
 */
export function eventOverlapsDay(event: TimelineEvent, day: Date): boolean {
  const dayStartMs = startOfDay(day).getTime()
  const dayEndMs = dayStartMs + MS_PER_DAY
  const s = event.start.getTime()
  const end = (event.end ?? event.start).getTime()
  const safeEnd = end > s ? end : s + 1
  return intervalsOverlap(s, safeEnd, dayStartMs, dayEndMs)
}

/**
 * Filter events to those that intersect the given local day, then clamp each to
 * the day's bounds so multi-day spillovers render as a partial block. Sorted by
 * (clamped) start, then by longer-duration-first for stable column packing.
 */
export function eventsForDay(events: Array<TimelineEvent>, day: Date): Array<{ event: TimelineEvent } & DaySpan> {
  const dayStartMs = startOfDay(day).getTime()
  const dayEndMs = dayStartMs + MS_PER_DAY

  return events
    .filter((e) => eventOverlapsDay(e, day))
    .map((event) => {
      const rawStart = event.start.getTime()
      const rawEnd = (event.end ?? event.start).getTime()
      const safeEnd = rawEnd > rawStart ? rawEnd : rawStart + 30 * MS_PER_MINUTE // give 0-length events a min height
      return {
        event,
        startMs: Math.max(rawStart, dayStartMs),
        endMs: Math.min(safeEnd, dayEndMs),
      }
    })
    .sort((a, b) => a.startMs - b.startMs || b.endMs - a.endMs)
}

/**
 * Column-pack overlapping blocks so concurrent events render side-by-side.
 *
 * Greedy interval graph coloring: blocks are processed in start order; each is
 * placed in the first column whose last-occupant has ended. The number of
 * columns a block reports (`columnCount`) is the max concurrency of the cluster
 * it belongs to — a maximal run of transitively-overlapping blocks — so every
 * block in a cluster shares the same width and they tile the row exactly.
 *
 * @param proposed  The proposed slot, used only to flag conflicts. Pass null to
 *                  flag nothing (e.g. when the proposed event is all-day).
 */
export function packColumns(
  spans: Array<{ event: TimelineEvent } & DaySpan>,
  proposed: DaySpan | null,
): Array<PositionedEvent> {
  // Assign each span a column via greedy coloring.
  const columnEnds: Array<number> = [] // columnEnds[i] = endMs of last block in column i
  const withColumns = spans.map((span) => {
    let column = columnEnds.findIndex((end) => end <= span.startMs)
    if (column === -1) {
      column = columnEnds.length
      columnEnds.push(span.endMs)
    } else {
      columnEnds[column] = span.endMs
    }
    return { ...span, column }
  })

  // Group into clusters of transitively-overlapping blocks. Because spans are
  // start-sorted, a new cluster begins whenever a block starts at/after the
  // running max-end of the current cluster.
  const result: Array<PositionedEvent> = []
  let clusterStartIndex = 0
  let clusterMaxEnd = -Infinity

  const flushCluster = (endIndex: number) => {
    const cluster = withColumns.slice(clusterStartIndex, endIndex)
    if (cluster.length === 0) return
    const columnCount = Math.max(...cluster.map((c) => c.column)) + 1
    for (const c of cluster) {
      result.push({
        ...c,
        columnCount,
        conflict: proposed ? intervalsOverlap(c.startMs, c.endMs, proposed.startMs, proposed.endMs) : false,
      })
    }
  }

  withColumns.forEach((span, index) => {
    if (span.startMs >= clusterMaxEnd) {
      flushCluster(index)
      clusterStartIndex = index
      clusterMaxEnd = span.endMs
    } else {
      clusterMaxEnd = Math.max(clusterMaxEnd, span.endMs)
    }
  })
  flushCluster(withColumns.length)

  return result
}

/**
 * Clamp the proposed [start, end] to the given day and express it as a DaySpan,
 * or null when the proposed range doesn't intersect the day at all (e.g. an
 * all-day event whose displayed span we don't want to draw as a timed band).
 */
export function proposedSpanForDay(day: Date, start: Date, end: Date): DaySpan | null {
  const dayStartMs = startOfDay(day).getTime()
  const dayEndMs = dayStartMs + MS_PER_DAY
  const s = start.getTime()
  const e = end.getTime() > s ? end.getTime() : s + 30 * MS_PER_MINUTE
  if (!intervalsOverlap(s, e, dayStartMs, dayEndMs)) return null
  return { startMs: Math.max(s, dayStartMs), endMs: Math.min(e, dayEndMs) }
}

/**
 * The full local day as a span (midnight → next midnight). Used as the
 * availability window for an all-day draft, which occupies the whole day and so
 * conflicts with any busy interval that day — even though it draws no timed band.
 */
export function fullDaySpan(day: Date): DaySpan {
  const startMs = startOfDay(day).getTime()
  return { startMs, endMs: startMs + MS_PER_DAY }
}

/**
 * The span the availability summary is judged against AND the window free/busy
 * is fetched for — the WHOLE proposed event, which may straddle multiple local
 * days. The {@link DayTimeline} only DRAWS one day, but conflict detection must
 * cover every day the draft touches or a guest busy on a later day would be
 * silently missed (false "Everyone free").
 *
 *  - timed: the exact `[start, end)` instant range. A degenerate (end ≤ start)
 *    range is given a 30-minute floor so it can still overlap a busy interval.
 *  - all-day: midnight of the start day → next midnight after the end day, so a
 *    multi-day all-day event covers every day it spans.
 */
export function eventAvailabilitySpan(start: Date, end: Date, allDay: boolean): DaySpan {
  if (allDay) {
    return { startMs: startOfDay(start).getTime(), endMs: startOfDay(end).getTime() + MS_PER_DAY }
  }
  const startMs = start.getTime()
  const endMs = end.getTime() > startMs ? end.getTime() : startMs + 30 * MS_PER_MINUTE
  return { startMs, endMs }
}

/** Fraction [0,1] of the day at which `ms` falls (0 = midnight, 1 = next midnight). */
export function dayFraction(day: Date, ms: number): number {
  const dayStartMs = startOfDay(day).getTime()
  return Math.min(1, Math.max(0, (ms - dayStartMs) / MS_PER_DAY))
}

/** Number of existing events that conflict with the proposed span. */
export function countConflicts(positioned: Array<PositionedEvent>): number {
  return positioned.filter((p) => p.conflict).length
}

// ---------------------------------------------------------------------------
// Attendee FREE/BUSY — "Phase 2" of the calendar overlay.
//
// The three-state model is defined by the backend `/v1/calendars/freebusy`
// endpoint and mirrored here byte-for-byte:
//  - 'busy'    — provider returned ≥1 busy interval in the window.
//  - 'free'    — provider returned the attendee with zero busy intervals.
//  - 'unknown' — provider erred (external/unshared calendar), the attendee was
//                absent, or the connection lacks the broad calendar scope.
//
// 'unknown' is NEVER treated as free — it renders distinctly (greyed/striped)
// and is EXCLUDED from the "everyone free / N conflicts" summary, which is
// computed only over attendees with KNOWN (busy|free) data.
// ---------------------------------------------------------------------------

export type FreeBusyStatus = 'busy' | 'free' | 'unknown'

/** A busy interval as returned by the backend (ISO-8601 strings). */
export interface BusyInterval {
  start: string
  end: string
}

/** Per-attendee availability, matching the backend response entry shape. */
export interface AttendeeFreeBusy {
  email: string
  status: FreeBusyStatus
  busy: Array<BusyInterval>
}

/** A single attendee's busy interval clamped to a day, ready to draw as a band. */
export interface AttendeeBusyBand extends DaySpan {
  email: string
}

/**
 * Clamp an attendee's busy intervals to the given local day, dropping intervals
 * that don't intersect it and any that are unparseable. Mirrors `eventsForDay`'s
 * clamping so attendee bands line up with the own-event blocks. Returns bands
 * sorted by start.
 *
 * Only `busy` attendees contribute bands: per the overlay contract, `unknown`
 * (we couldn't read the calendar) and `free` attendees must never render violet
 * busy bands. Either may still carry a non-empty `busy` array, so we gate on
 * status here rather than trusting the array to be empty.
 */
export function attendeeBusyBandsForDay(attendee: AttendeeFreeBusy, day: Date): Array<AttendeeBusyBand> {
  if (attendee.status !== 'busy') return []

  const dayStartMs = startOfDay(day).getTime()
  const dayEndMs = dayStartMs + MS_PER_DAY

  return attendee.busy
    .map((b) => ({ startMs: Date.parse(b.start), endMs: Date.parse(b.end) }))
    .filter((b) => Number.isFinite(b.startMs) && Number.isFinite(b.endMs) && b.endMs > b.startMs)
    .filter((b) => intervalsOverlap(b.startMs, b.endMs, dayStartMs, dayEndMs))
    .map((b) => ({
      email: attendee.email,
      startMs: Math.max(b.startMs, dayStartMs),
      endMs: Math.min(b.endMs, dayEndMs),
    }))
    .sort((a, b) => a.startMs - b.startMs)
}

/**
 * Whether an attendee is busy during the proposed span. `unknown` attendees are
 * NOT a conflict (we don't know), only `busy` overlaps count.
 */
export function attendeeConflictsWithProposed(attendee: AttendeeFreeBusy, proposed: DaySpan | null): boolean {
  if (!proposed || attendee.status !== 'busy') return false
  return attendee.busy.some((b) => {
    const s = Date.parse(b.start)
    const e = Date.parse(b.end)
    if (!Number.isFinite(s) || !Number.isFinite(e)) return false
    return intervalsOverlap(s, e, proposed.startMs, proposed.endMs)
  })
}

/** The availability summary, computed only over attendees with KNOWN data. */
export interface AvailabilitySummary {
  /** Attendees whose status is busy or free (drives the headline). */
  knownCount: number
  /** Attendees whose status is `busy` (have ≥1 busy interval that day). */
  busyCount: number
  /** Attendees whose status is `free`. */
  freeCount: number
  /** Attendees whose status is unknown (shown separately, never as free). */
  unknownCount: number
  /** Known attendees who conflict with the proposed span. */
  conflictCount: number
  /** True when there is ≥1 known attendee and none of them conflict. */
  everyoneFree: boolean
}

/**
 * Summarize attendee availability against the proposed span. Two distinct axes:
 *
 *  - The "everyone free" / "N conflicts" HEADLINE is derived ONLY from attendees
 *    with KNOWN (busy|free) data and is about the PROPOSED SLOT — an `unknown`
 *    attendee can neither make everyone free nor count as a conflict, and a
 *    `busy` attendee whose intervals don't overlap the slot does NOT conflict.
 *    With zero known attendees, `everyoneFree` is false (nothing to assert).
 *  - The `busyCount`/`freeCount`/`unknownCount` LEGEND counts are by STATUS, not
 *    by slot conflict, so they line up with the three-state model and the violet
 *    busy bands (drawn for every `busy` attendee). A guest busy elsewhere that
 *    day is `busy` here even if they don't conflict with the proposed slot.
 */
export function summarizeAvailability(
  attendees: Array<AttendeeFreeBusy>,
  proposed: DaySpan | null,
): AvailabilitySummary {
  const known = attendees.filter((a) => a.status === 'busy' || a.status === 'free')
  const busyCount = attendees.filter((a) => a.status === 'busy').length
  const freeCount = attendees.filter((a) => a.status === 'free').length
  const unknownCount = attendees.length - known.length
  const conflictCount = known.filter((a) => attendeeConflictsWithProposed(a, proposed)).length
  return {
    knownCount: known.length,
    busyCount,
    freeCount,
    unknownCount,
    conflictCount,
    everyoneFree: known.length > 0 && conflictCount === 0,
  }
}

/** A guest's availability FOR THE PROPOSED SLOT (the question the user has). */
export type SlotAvailability = 'busy' | 'free' | 'unknown'

/** One per-attendee row for the availability footer. */
export interface AttendeeRow {
  email: string
  /**
   * Availability for the proposed slot — the only thing the footer states:
   *  - `busy`    — a known attendee whose busy intervals overlap the slot.
   *  - `free`    — a known attendee who does NOT overlap the slot (whether or not
   *                they're busy at OTHER times today — that's just noise for the
   *                decision; the timeline bands show those periods on hover).
   *  - `unknown` — we couldn't read their calendar.
   */
  slot: SlotAvailability
}

const SLOT_ORDER: Record<SlotAvailability, number> = { busy: 0, free: 1, unknown: 2 }

/**
 * Per-attendee rows for the availability footer, framed around the PROPOSED SLOT
 * rather than raw status, and ordered busy → free → unknown so conflicts surface
 * first. (Array#sort is stable, so attendees keep their input order within a
 * bucket.) This is the data the footer renders instead of abstract status counts.
 */
export function attendeeAvailabilityRows(
  attendees: Array<AttendeeFreeBusy>,
  proposed: DaySpan | null,
): Array<AttendeeRow> {
  return attendees
    .map((a): AttendeeRow => {
      if (a.status === 'unknown') return { email: a.email, slot: 'unknown' }
      return { email: a.email, slot: attendeeConflictsWithProposed(a, proposed) ? 'busy' : 'free' }
    })
    .sort((x, y) => SLOT_ORDER[x.slot] - SLOT_ORDER[y.slot])
}
